import { createSelector } from '@reduxjs/toolkit';
import { CampaignPlanAllocationReportDealLineSelectConfig } from 'components/common/types/CampaignPlan.types';
import { Dsp } from 'components/common/types/Deal.types';
import { DirectSalesMediaType, DirectSalesPricingMode } from 'components/common/types/DirectSalesCampaign.types';
import { PublisherDspsMediaOwner, PublisherType } from 'components/common/types/Publisher.types';
import { Store } from 'components/common/types/Store.types';
import { getPublisherDefaultCriteria, TargetCriteria } from 'utils/getPublisherDefaultCriteria';
import { resolvePublisherDefaultNonNetworkDirectSalesPricingMode } from 'utils/resolvePublisherDefaultDirectSalesPricingMode';

export const getIsReseller = createSelector([(state: Store) => state.publisher], (publisher) => {
  const { type } = publisher.configuration;
  return type === PublisherType.RESELLER;
});

export const getPublisherTargetCriteria = createSelector(
  [(state: Store) => state.publisher.targetTolerances],
  (targetTolerances): TargetCriteria => getPublisherDefaultCriteria(targetTolerances),
);

export const getExclusiveDsps = createSelector(
  [(state: Store) => state.publisher.dsps, (state: Store) => state.reseller.exclusiveDsps],
  (dsps, exclusiveDsps) => {
    const validDsps = exclusiveDsps.length > 0 ? exclusiveDsps : Object.keys(dsps);
    return validDsps.reduce(
      (acc, dsp) => {
        if (dsps[dsp]) {
          acc[dsp] = dsps[dsp];
        }
        return acc;
      },
      {} as unknown as PublisherDspsMediaOwner,
    );
  },
);

export const getDsps = createSelector([getExclusiveDsps], (exclusiveDsps): Dsp[] => Object.values(exclusiveDsps));

export const getDefaultDirectSalesPricingModeForMediaType = (
  state: Store,
  mediaType?: DirectSalesMediaType,
): DirectSalesPricingMode.CPM | DirectSalesPricingMode.PANEL =>
  resolvePublisherDefaultNonNetworkDirectSalesPricingMode(state.publisher.campaignConfiguration, mediaType);

export const getDefaultDirectSalesPricingModeForLine = (
  state: Store,
  lineId: string,
): DirectSalesPricingMode.CPM | DirectSalesPricingMode.PANEL => {
  const line = state.dealManagement.backupFormData.lines.find((l) => l.lineId === lineId);
  const mediaType = line && 'mediaType' in line ? line.mediaType : undefined;

  return getDefaultDirectSalesPricingModeForMediaType(state, mediaType);
};

export const getDirectSalesLinePricingMode = (state: Store, lineId: string): DirectSalesPricingMode | undefined => {
  const line = state.dealManagement.backupFormData.lines.find((l) => l.lineId === lineId);

  if (line && 'pricingMode' in line && line.pricingMode) {
    return line.pricingMode;
  }

  if (state.dealManagement.commonDeal.currentLine.lineId === lineId) {
    return state.dealManagement.directSales.pricingMode;
  }

  return undefined;
};

export const getLocaleCode = createSelector(
  [(state: Store) => state.publisher.configuration.localeCode],
  (localeCode) => localeCode,
);

export const getCurrencyCode = createSelector(
  [(state: Store) => state.publisher.configuration.currencyCode],
  (currencyCode) => currencyCode,
);

export const getTimezone = createSelector(
  [(state: Store) => state.publisher.configuration.timezone],
  (timezone) => timezone,
);

export const getIso2CountryCode = createSelector(
  [(state: Store) => state.publisher.configuration.iso2CountryCode],
  (iso2CountryCode) => iso2CountryCode,
);

export const getFlag = createSelector([(state: Store) => state.publisher.configuration.flag], (flag) => flag);

export const getMarketId = createSelector(
  [(state: Store) => state.publisher.configuration.marketId],
  (marketId) => marketId,
);

export const getAllocationReportDealLineSelect = createSelector(
  [(state: Store) => state.publisher.configuration],
  (configuration): CampaignPlanAllocationReportDealLineSelectConfig => ({
    isDefault: configuration.isAllocationReportSelectAllDefault ?? false,
    isLocked: configuration.isAllocationReportSelectAllLocked ?? false,
    isHidden: configuration.isAllocationReportSelectAllHidden ?? false,
  }),
);
